import { useEffect, useMemo, useState } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import { hasValidToken } from 'src/utils/authUtils';
import { useQuery } from 'react-query';
import { GenericResponseWithPayload } from 'src/types/generic';
import { handleGlobalSearchRedirect } from 'src/utils/redirectUtils';
import { RoutesType } from '../../utils/routes';
import {
  CommandMenuDialog,
  CommandMenuFooter,
  CommandMenuFooterText,
  CommandMenuItem,
  CommandMenuKeyboardKey,
  CommandMenuListItemAdditionalInfo,
  CommandMenuListItemText,
  CommandMenuLoading,
  CommandMenuSearchInput,
  CommandMenuSectionTitle,
  CommandNoResults,
} from './StyledCommandMenu';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { GlobalSearchItem, GlobalSearchResponse } from './globalSearch/types/globalSearchTypes';
import { fetchGlobalSearchResults } from './globalSearch/requests/globalSearchRequests';
import {
  groupGlobalSearchItems,
  isPlayerRoute,
  MIN_SEARCH_INPUT_LENGTH,
  typeToLabelMap,
} from './globalSearch/utils/globalSearchUtils';
import { setCommandMenuOpen, toggleCommandMenu } from '../../reducers/commandMenuSlice';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

const CommandMenu = (): JSX.Element => {
  const [searchValue, setSearchValue] = useState('');
  const dispatch = useAppDispatch();
  const open = useAppSelector((state) => state.commandMenu.open);

  const navigate = useNavigate();

  const redirect = (path: string): void => {
    navigate(path);
    dispatch(setCommandMenuOpen(false));
  };

  const { location } = window;

  const isAuthenticated = hasValidToken();

  const normalizedInput = searchValue.trim();

  const {
    data: searchResults,
    isLoading,
  } = useQuery<GenericResponseWithPayload<GlobalSearchResponse>>(
    ['globalSearch', normalizedInput],
    () => fetchGlobalSearchResults(normalizedInput),
    {
      enabled: normalizedInput.length >= MIN_SEARCH_INPUT_LENGTH,
      refetchOnWindowFocus: false,
    },
  );

  const globalSearchItems = searchResults?.data?.Results || [];
  const groupedGlobalSearchItems = useMemo(() => groupGlobalSearchItems(globalSearchItems), [globalSearchItems]);

  useEffect(() => {
    const down = (e: KeyboardEvent): void => {
      const isWBFRender = (location.ancestorOrigins && location.ancestorOrigins.length > 0);
      if (e.key === 'k' && (e.metaKey || e.ctrlKey) && isAuthenticated && !isWBFRender) {
        e.preventDefault();
        dispatch(toggleCommandMenu());
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [isAuthenticated]);

  const mapRoutesToCommandItems = (items: RoutesType[]): (JSX.Element | null)[] | null => {
    if (!items || items.length === 0) return null;

    return items.flatMap((item) => {
      if (item.path) {
        return (
          <CommandMenuItem
            onSelect={() => {
              if (item.path) {
                redirect(item.path);
                setSearchValue('');
              }
            }}
            keywords={item.title ? [item.title] : undefined}
            key={item.title}
          >
            <CommandMenuListItemText>{item.title}</CommandMenuListItemText>
          </CommandMenuItem>
        );
      }

      if (item.subitems && item.subitems.length > 0) {
        return mapRoutesToCommandItems(item.subitems);
      }

      return null;
    });
  };

  const mapSearchResultsToCommandItems = (items: GlobalSearchItem[]): JSX.Element[] => items.map((item) => (
    <CommandMenuItem
      key={`global-${item.Type}-${item.DataUuid}`}
      value={`${item.Data} ${item.DataUuid}`}
      onSelect={() => {
        handleGlobalSearchRedirect(item);
        dispatch(setCommandMenuOpen(false));
        setSearchValue('');
      }}
    >
      <CommandMenuListItemText>{item.Data}</CommandMenuListItemText>
      {isPlayerRoute(item.Type) && (
        <CommandMenuListItemAdditionalInfo>{`${item.AdditionalInfo.FranchiseName}`}</CommandMenuListItemAdditionalInfo>
      )}
    </CommandMenuItem>
  ));

  const userRoutes = useAppSelector((state) => state.userInfo.Routes) ?? [];

  return (
    <CommandMenuDialog
      open={open}
      onOpenChange={(isOpen) => {
        dispatch(setCommandMenuOpen(isOpen));
        if (!isOpen) setSearchValue('');
      }}
    >
      <CommandMenuSearchInput
        autoFocus
        placeholder="Search pages, player login, ID or external ID..."
        value={searchValue}
        onValueChange={setSearchValue}
      />

      <Command.List>
        {isLoading && (
        <CommandMenuLoading>
          <LoadingSpinner size="sm" />
          Loading...
        </CommandMenuLoading>
        )}

        {!isLoading && <CommandNoResults>No results found</CommandNoResults> }

        <CommandMenuSectionTitle key="Pages" heading="Pages">
          {mapRoutesToCommandItems(userRoutes)}
        </CommandMenuSectionTitle>

        {normalizedInput.length >= MIN_SEARCH_INPUT_LENGTH && !isLoading && groupedGlobalSearchItems.map(([type, items]) => (
          <CommandMenuSectionTitle key={type} heading={typeToLabelMap[type]}>
            {mapSearchResultsToCommandItems(items)}
          </CommandMenuSectionTitle>
        ))}

      </Command.List>

      <CommandMenuFooter>
        <CommandMenuFooterText>
          Global Search: enter at least
          {' '}
          {MIN_SEARCH_INPUT_LENGTH}
          {' '}
          symbols
        </CommandMenuFooterText>
        <CommandMenuFooterText>
          Open Page
          <CommandMenuKeyboardKey>⏎</CommandMenuKeyboardKey>
        </CommandMenuFooterText>
      </CommandMenuFooter>
    </CommandMenuDialog>
  );
};

export default CommandMenu;
